/**
 * Налаштування карткового (kanban) виду — які поля показувати на картці,
 * чи показувати підпис поля, та розмір картки. Персистується в localStorage
 * per-table (ключ = slug).
 */

export type CardSize = 'small' | 'medium' | 'large';

export interface KanbanCardField {
  key: string;
  /** Показувати підпис поля (label:) перед значенням. */
  showLabel: boolean;
}

export interface KanbanCardConfig {
  /** Поле-заголовок картки (велике, зверху). null = primary/перше поле. */
  primaryFieldKey: string | null;
  /** Додаткові поля під заголовком, у порядку. */
  fields: KanbanCardField[];
  cardSize: CardSize;
}

const KEY = (slug: string) => `kanban-card-config:${slug}`;

export function loadCardConfig(slug: string): KanbanCardConfig | null {
  try {
    const raw = localStorage.getItem(KEY(slug));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as KanbanCardConfig;
    if (!parsed || !Array.isArray(parsed.fields)) return null;
    return {
      primaryFieldKey: parsed.primaryFieldKey ?? null,
      fields: parsed.fields.filter((f) => f && typeof f.key === 'string'),
      cardSize: parsed.cardSize ?? 'medium',
    };
  } catch {
    return null;
  }
}

export function saveCardConfig(slug: string, config: KanbanCardConfig): void {
  try {
    localStorage.setItem(KEY(slug), JSON.stringify(config));
  } catch {
    // ignore
  }
}

/* ── View mode (list/kanban) persistence per-table ─────────────────────────── */

export type KanbanGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

export interface ViewModeState {
  viewMode: 'list' | 'kanban';
  kanbanFieldKey: string | null;
  /** Гранулярність для date/datetime групування. */
  granularity?: KanbanGranularity;
}

const VM_KEY = (slug: string) => `table-view-mode:${slug}`;

export function loadViewMode(slug: string): ViewModeState | null {
  try {
    const raw = localStorage.getItem(VM_KEY(slug));
    if (!raw) return null;
    const p = JSON.parse(raw) as ViewModeState;
    return {
      viewMode: p.viewMode === 'kanban' ? 'kanban' : 'list',
      kanbanFieldKey: p.kanbanFieldKey ?? null,
      granularity: p.granularity ?? 'month',
    };
  } catch {
    return null;
  }
}

export function saveViewMode(slug: string, state: ViewModeState): void {
  try {
    localStorage.setItem(VM_KEY(slug), JSON.stringify(state));
  } catch {
    // ignore
  }
}

/* ── Column widths persistence (per table + group field) ───────────────────── */

const CW_KEY = (slug: string, fieldKey: string) => `kanban-col-widths:${slug}:${fieldKey}`;

export function loadColumnWidths(slug: string, fieldKey: string): Record<string, number> {
  try {
    const raw = localStorage.getItem(CW_KEY(slug, fieldKey));
    if (!raw) return {};
    const p = JSON.parse(raw) as Record<string, number>;
    return p && typeof p === 'object' ? p : {};
  } catch {
    return {};
  }
}

export function saveColumnWidths(slug: string, fieldKey: string, widths: Record<string, number>): void {
  try {
    localStorage.setItem(CW_KEY(slug, fieldKey), JSON.stringify(widths));
  } catch {
    // ignore
  }
}

/* ── Card-layout presets (named field layouts) ────────────────────────────── */

export interface KanbanPreset {
  name: string;
  config: KanbanCardConfig;
}

const PRESETS_KEY = (slug: string) => `kanban-presets:${slug}`;

export function loadPresets(slug: string): KanbanPreset[] {
  try {
    const raw = localStorage.getItem(PRESETS_KEY(slug));
    if (!raw) return [];
    const p = JSON.parse(raw) as KanbanPreset[];
    return Array.isArray(p) ? p.filter((x) => x && typeof x.name === 'string' && x.config) : [];
  } catch {
    return [];
  }
}

export function savePreset(slug: string, preset: KanbanPreset): KanbanPreset[] {
  const list = loadPresets(slug).filter((p) => p.name !== preset.name);
  list.push(preset);
  try {
    localStorage.setItem(PRESETS_KEY(slug), JSON.stringify(list));
  } catch {
    // ignore
  }
  return list;
}

export function deletePreset(slug: string, name: string): KanbanPreset[] {
  const list = loadPresets(slug).filter((p) => p.name !== name);
  try {
    localStorage.setItem(PRESETS_KEY(slug), JSON.stringify(list));
  } catch {
    // ignore
  }
  return list;
}
